import React, { useState } from 'react';
import { StatusChange } from '../types';
import { statusColorMap } from '../util/statusColors';

interface OrderStatusTimelineProps {
  statusHistory: StatusChange[];
  currentStatus: string;
}

const OrderStatusTimeline: React.FC<OrderStatusTimelineProps> = ({ statusHistory, currentStatus }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatUsername = (email: string | null) => {
    if (!email) return 'System';
    const username = email.split('@')[0];
    // Capitalize first letter and replace dots/underscores with spaces
    return username
      .split(/[._-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getCircleColor = (status: string | null) => {
    if (!status) return 'bg-green-600';
    const colorMap: Record<string, string> = {
      submitted: 'bg-gray-400',
      paid: 'bg-red-400',
      items_purchased: 'bg-orange-400',
      ready_for_collection: 'bg-yellow-400',
      collected: 'bg-green-400',
      cancelled: 'bg-purple-400',
    };
    return colorMap[status] || 'bg-blue-400';
  };

  // If there's no history, show the current status as the initial state
  if (!statusHistory || statusHistory.length === 0) {
    return (
      <div className={`bg-white shadow rounded-md w-full md:w-auto md:flex-1 ${!isExpanded ? 'flex items-center' : 'p-4'}`}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`flex items-center justify-between w-full text-left whitespace-nowrap ${!isExpanded ? 'px-4 py-2' : ''}`}
        >
          <h3 className="text-lg font-semibold">Status History</h3>
          <svg
            className={`w-5 h-5 ml-2 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isExpanded && (
          <div className="mt-4">
            <div className="flex items-start">
              <div className="flex flex-col items-center mr-4">
                <div className={`w-4 h-4 rounded-full ${getCircleColor(currentStatus)}`}></div>
                <div className="w-0.5 h-full bg-gray-300 mt-2"></div>
              </div>
              <div className="flex-1 pb-8">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-1 rounded text-sm font-medium bg-gray-100 text-black">
                    {statusColorMap[currentStatus]?.text || currentStatus}
                  </span>
                </div>
                <p className="text-sm text-gray-500">Current status</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white shadow rounded-md w-full md:w-auto md:flex-1 ${!isExpanded ? 'flex items-center' : 'p-4'}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center justify-between w-full text-left whitespace-nowrap ${!isExpanded ? 'px-4 py-2' : ''}`}
      >
        <h3 className="text-lg font-semibold">Status History ({statusHistory.length} change{statusHistory.length !== 1 ? 's' : ''})</h3>
        <svg
          className={`w-5 h-5 ml-2 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="mt-4 space-y-0">
          {statusHistory.map((change, index) => {
            const isLast = index === statusHistory.length - 1;
            return (
              <div key={index} className="flex items-start">
                <div className="flex flex-col items-center mr-4">
                  <div className={`w-4 h-4 rounded-full ${getCircleColor(change.to)}`}></div>
                  {!isLast && <div className="w-0.5 h-full bg-gray-300 mt-2" style={{ minHeight: '60px' }}></div>}
                </div>
                <div className="flex-1 pb-8">
                  {change.note ? (
                    <p className="text-sm font-medium text-gray-900 mb-1">{change.note}</p>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                      {change.from && (
                        <>
                          <span className="px-2 py-1 rounded text-sm bg-gray-100 text-black">
                            {statusColorMap[change.from]?.text || change.from}
                          </span>
                          <span className="text-gray-400 text-sm">→</span>
                        </>
                      )}
                      <span className="px-2 py-1 rounded text-sm font-medium bg-gray-100 text-black">
                        {change.to && (statusColorMap[change.to]?.text || change.to)}
                      </span>
                      {change.notification_status === 'sent' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Notification Sent
                        </span>
                      )}
                      {change.notification_status === 'error' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Notification Error
                        </span>
                      )}
                      {change.notification_status === 'none' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          No Notification
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-gray-600 mt-1">{formatDate(change.changed_at)}</p>
                  <p className="text-xs text-gray-500 mt-1">by {formatUsername(change.changed_by)}</p>
                  {change.reason && (
                    <p className="text-sm text-gray-700 mt-1">Reason: {change.reason}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderStatusTimeline;
